
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Icon from '@/components/ui/Icon';
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import NotificationIndicator from '@/components/ui/NotificationBadge';
import useActivityNotifications from '@/hooks/useActivityNotifications';
import FeedbackButton from '@/components/feedback/FeedbackButton';
import FeedbackModal from '@/components/feedback/FeedbackModal';
import risingSquadLogo from '@/assets/rising-squad.png';
import ProfilePicture from '@/components/ProfilePicture';
import { useThemeSettings } from "@/contexts/ThemeContext";
import { Shield } from 'lucide-react';
import { Ripple } from '@/components/ui/Ripple';
import { isWebBrowser } from '@/utils/deviceUtils';

export default function Layout({ children, currentPageName }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const { getTotalUnreadCount } = useActivityNotifications();
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const { theme } = useThemeSettings();

  const isDarkTheme = theme === 'dark';
  const layoutBackgroundClass = isDarkTheme
    ? 'min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100'
    : 'min-h-screen bg-gray-100 text-slate-900';
  const sidebarContainerClasses = isDarkTheme
    ? 'bg-gradient-to-br from-slate-950/95 via-slate-900/90 to-slate-950/95 backdrop-blur-xl border-r-2 border-slate-700/40 text-slate-100 shadow-[0_12px_32px_-12px_rgba(2,6,23,0.75)]'
    : 'bg-[linear-gradient(to_bottom,rgba(219,234,254,0.4)_0%,rgba(209,250,229,0.4)_33%,rgba(254,249,195,0.4)_66%,rgba(243,232,255,0.4)_100%)] backdrop-blur-xl border-r-2 border-slate-200/60 text-slate-900 shadow-lg';
  const navItemClasses = (active) => {
    if (active) {
      return isDarkTheme
        ? 'bg-slate-800 text-white shadow-lg shadow-slate-900/40'
        : 'bg-brand-500 text-white shadow-lg shadow-[0_12px_20px_-10px_rgba(109,40,217,0.45)]';
    }

    return isDarkTheme
      ? 'text-slate-200 hover:text-white hover:bg-slate-800/90 hover:shadow-inner'
      : 'text-slate-900 hover:text-slate-900 hover:bg-white/70';
  };
  const adminLinkInactive = isDarkTheme
    ? 'text-slate-200 hover:text-white hover:bg-slate-800/90'
    : 'text-slate-900 hover:text-slate-900 hover:bg-white/70';
  const signOutButtonClasses = isDarkTheme
    ? 'text-slate-200 hover:bg-red-500/10 hover:text-red-300'
    : 'text-slate-900 hover:bg-red-100 hover:text-red-700';
  const headerBackgroundClasses = isDarkTheme
    ? 'bg-[linear-gradient(to_right,rgb(2,6,23)_0%,rgb(15,23,42)_50%,rgb(2,6,23)_100%)] text-slate-100 shadow-[0_12px_32px_-12px_rgba(2,6,23,0.6)] border-b border-slate-800/60'
    : 'bg-[linear-gradient(to_right,rgba(219,234,254,0.6)_0%,rgba(209,250,229,0.6)_33%,rgba(254,249,195,0.6)_66%,rgba(243,232,255,0.6)_100%)] md:bg-[linear-gradient(to_right,rgba(219,234,254,0.6)_0%,rgba(209,250,229,0.6)_33%,rgba(254,249,195,0.6)_66%,rgba(243,232,255,0.6)_100%)] backdrop-blur-lg border-b border-white/60 text-slate-900';
  const headerTitleClasses = isDarkTheme ? 'text-slate-100' : 'text-slate-900';
  const mobileNavBackgroundClasses = isDarkTheme
    ? 'bg-[linear-gradient(135deg,rgb(2,6,23)_0%,rgb(15,23,42)_50%,rgb(2,6,23)_100%)] backdrop-blur-md border-t border-slate-800/60'
    : 'bg-white/90 backdrop-blur-md border-t border-purple-100';
  const mobileNavTextClasses = (active) => active
    ? 'text-brand-500 font-semibold'
    : isDarkTheme ? 'text-slate-300' : 'text-slate-700';
  const headerControlButtonClasses = isDarkTheme
    ? 'p-3 rounded-md text-slate-200 hover:bg-slate-800 transition-colors min-w-[48px] min-h-[48px] flex items-center justify-center'
    : 'p-3 rounded-md text-slate-600 hover:bg-white/60 transition-colors min-w-[48px] min-h-[48px] flex items-center justify-center';
  const mainBackgroundClasses = isDarkTheme
    ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950'
    : 'bg-gray-100';

  const handleLogout = async () => {
    try {
      // Sign out - this clears user state immediately, which will trigger
      // ProtectedRoute to redirect to /auth/login via Navigate component
      await signOut();
    } catch (error) {
      console.error('Logout error:', error);
      // Even on error, signOut() clears user state, so ProtectedRoute will redirect
    }
  };

  const handleFeedbackClick = () => {
    setIsFeedbackModalOpen(true);
  };

  const handleCloseFeedbackModal = () => {
    setIsFeedbackModalOpen(false);
  };

  // Handle logo click - navigate to landing page for web users only
  const handleLogoClick = () => {
    if (isWebBrowser()) {
      navigate('/landing');
    }
    // For mobile app users, do nothing (logo click is disabled)
  };

  // Don't show layout on Landing page
  if (currentPageName === "Landing") {
    return children;
  }

  const navigationItems = [
    {
      name: "Dashboard",
      url: "/Dashboard",
      icon: "home",
      label: "Dashboard",
      mobileLabel: "Home" // Shorter label for mobile
    },
    {
      name: "MyPrograms",
      url: "/MyPrograms",
      icon: "heart",
      label: "My Programs",
      mobileLabel: "Programs"
    },
    {
      name: "MyCircle",
      url: "/MyCircle",
      icon: "users",
      label: "My Squad",
      mobileLabel: "My Squad"
    },
    {
      name: "Calendar",
      url: "/Calendar",
      icon: "calendar",
      label: "Planning",
      mobileLabel: "Planning"
    },
    {
      name: "Discovery",
      url: "/Discovery",
      icon: "search",
      label: "Discover",
      mobileLabel: "Discover"
    }
  ];

  // Map page names to header titles
  const getHeaderTitle = () => {
    const pageTitleMap = {
      "Dashboard": "Dashboard",
      "Discovery": "Discover", 
      "MyPrograms": "My Programs",
      "Calendar": "Planning",
      "MyCircle": "My Squad",
      "Settings": "Settings",
      "Profile": "Profile"
    };
    
    return pageTitleMap[currentPageName] || "Summer Squad";
  };

  return (
    <div className={layoutBackgroundClass}>
      <style>
        {`
          :root {
            --primary-purple: #685EFF;
            --primary-teal: #0d9488;
            --primary-yellow: #f59e0b;
            --primary-orange: #ea580c;
            --gradient-primary: linear-gradient(135deg, #1e40af 0%, #0d9488 25%, #685EFF 50%, #ec4899 75%, #f59e0b 100%);
            --shadow-card: 0 2px 12px -2px rgba(104, 94, 255, 0.08);
            --shadow-card-hover: 0 4px 24px -4px rgba(104, 94, 255, 0.15);
            --shadow-card-strong: 0 18px 45px -15px rgba(76, 29, 149, 0.28);
            --shadow-card-strong-hover: 0 22px 65px -18px rgba(76, 29, 149, 0.36);
          }
          
          .gradient-primary {
            background: var(--gradient-primary);
          }
          
          .card-shadow {
            box-shadow: var(--shadow-card);
          }
          
          .card-shadow:hover {
            box-shadow: var(--shadow-card-hover);
          }

          .child-card-shadow {
            box-shadow: var(--shadow-card-strong);
          }

          .child-card-shadow:hover {
            box-shadow: var(--shadow-card-strong-hover);
          }
          
          .text-gradient {
            background: var(--gradient-primary);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }
          
          .leaflet-container {
            height: 100vh !important;
            width: 100% !important;
            z-index: 1;
          }
          
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          
          .custom-camp-marker {
            background: transparent !important;
            border: none !important;
          }
          
          .user-location-marker {
            background: transparent !important;
            border: none !important;
          }
          
          /* Fallback for browsers that don't support backdrop-filter */
          @supports not (backdrop-filter: blur(1px)) {
            header[class*="backdrop-blur"],
            .backdrop-blur-lg,
            .backdrop-blur-md {
              background: linear-gradient(
                to right,
                rgba(219, 234, 254, 0.98) 0%,
                rgba(209, 250, 229, 0.98) 33%,
                rgba(254, 249, 195, 0.98) 66%,
                rgba(243, 232, 255, 0.98) 100%
              ) !important;
            }
          }
          
        `}
      </style>

      {/* Sidebar Navigation - Tablet and Desktop */}
      <div className="hidden md:fixed md:inset-y-0 md:left-0 md:z-50 md:w-[88px] lg:w-[230px] md:flex md:flex-col overflow-x-hidden">
              <div className={`flex grow flex-col gap-y-5 ${sidebarContainerClasses} px-0 md:px-4 lg:px-6 pb-4`}>
          {/* Sidebar Header */}
          <div className="flex h-16 shrink-0 items-center justify-center lg:justify-start">
            <div 
              className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity duration-200 min-w-0"
              onClick={handleLogoClick}
              title={isWebBrowser() ? "Go to Summer Squad homepage" : ""}
            >
              <div className="w-8 h-8 rounded-xl flex items-center justify-center overflow-hidden shadow-sm flex-shrink-0">
                <img
                  src={risingSquadLogo}
                  alt="Summer Squad logo"
                  className="w-full h-full object-contain p-0.5"
                  decoding="async"
                  loading="eager"
                  width={32}
                  height={32}
                />
              </div>
              <span className={`text-lg font-bold hidden lg:block leading-tight brand-font truncate ${isDarkTheme ? 'text-white' : 'text-slate-900'}`} aria-label="Summer Squad">Summer Squad</span>
            </div>
          </div>
          
          {/* Sidebar Navigation */}
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="md:mx-0 lg:-mx-2 space-y-1 md:flex md:flex-col md:items-center md:gap-3 md:w-full">
                  {navigationItems.map((item) => {
                    const isActive = location.pathname === item.url;

                    return (
                      <li key={item.name} className="md:w-full">
                        <button
                          onClick={() => navigate(item.url)}
                          className={`group flex gap-x-3 rounded-lg px-4 py-2 lg:px-3 lg:py-2 text-sm leading-6 font-semibold transition-all duration-200 justify-center lg:justify-start md:w-full ${
                            isDarkTheme ? 'bg-transparent' : ''
                          } ${navItemClasses(isActive)}`}
                          title={item.label}
                        >
                          <Icon
                            name={item.icon}
                            size={window.innerWidth >= 1024 ? "md" : "lg"}
                            className={isActive ? 'text-white' : isDarkTheme ? 'text-slate-300' : 'text-slate-600'}
                          />
                          <div className="hidden lg:flex lg:items-center lg:gap-2">
                            <span>{item.label}</span>
                            {item.comingSoon && (
                              <span className="px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-600 rounded-full font-normal">
                                Coming Soon
                              </span>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </li>
              
              {/* Spacer */}
              <li className="mt-auto space-y-1">
                {/* Admin Dashboard - Only show for admin users */}
                {(() => {
                  const userEmail = user?.email || '';
                  const isAdmin = userEmail === 'admin@campconnect.app' || userEmail.includes('@campconnect.app');
                  return isAdmin && (
                    <Link
                      to="/admin"
                      className={`group md:mx-0 lg:-mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 transition-all duration-200 justify-center lg:justify-start ${
                        location.pathname === '/admin' || location.pathname.startsWith('/admin/')
                          ? 'bg-brand-500 text-white shadow-md'
                          : adminLinkInactive
                      }`}
                      title="Admin Dashboard"
                    >
                      <Shield
                        className={`w-5 h-5 ${
                          location.pathname === '/admin' || location.pathname.startsWith('/admin/')
                            ? 'text-white'
                            : isDarkTheme ? 'text-slate-300' : 'text-slate-600'
                        }`}
                      />
                      <span className="hidden lg:inline">Admin Dashboard</span>
                    </Link>
                  );
                })()}

                {/* Analytics Test and Debug Tools are now integrated into the Admin Dashboard */}
                
                <button
                  onClick={handleLogout}
                  className={`group md:mx-0 lg:-mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 transition-all duration-200 w-full text-left justify-center lg:justify-start ${signOutButtonClasses}`}
                  title="Sign out"
                >
                  <Icon
                    name="logout"
                    size="md"
                    className={isDarkTheme ? 'text-slate-300' : 'text-slate-600'}
                  />
                  <span className="hidden lg:inline">Sign out</span>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
      
      {/* Header for authenticated users - adjusted for sidebar */}
      {/* Skip header for Calendar page since it has its own navigation */}
      {currentPageName !== "Calendar" && (
        <header className={`${headerBackgroundClasses} shadow-sm sticky z-40 md:pl-[88px] lg:pl-[230px] top-0`}>
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              {/* Mobile: Logo + Brand Name */}
              <div 
                className="flex items-center gap-3 md:hidden cursor-pointer hover:opacity-80 transition-opacity duration-200"
                onClick={handleLogoClick}
                title={isWebBrowser() ? "Go to Summer Squad homepage" : ""}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden">
                  <img
                    src={risingSquadLogo}
                    alt="Summer Squad logo"
                    className="w-full h-full object-contain"
                    decoding="async"
                    loading="eager"
                    width={40}
                    height={40}
                  />
                </div>
                <span className={`hidden sm:inline text-2xl sm:text-2xl font-bold brand-font ${headerTitleClasses}`}>Summer Squad</span>
              </div>
              
              {/* Tablet: Brand Name Only (no logo) */}
              <div 
                className="hidden md:flex lg:hidden items-center cursor-pointer hover:opacity-80 transition-opacity duration-200"
                onClick={handleLogoClick}
                title={isWebBrowser() ? "Go to Summer Squad homepage" : ""}
              >
                <span className={`text-2xl sm:text-2xl font-bold brand-font ${headerTitleClasses}`}>Summer Squad</span>
              </div>
              
              {/* Profile - Desktop (≥1024px) */}
              <div className="hidden lg:flex items-center gap-2 mr-2 ml-auto">
                <Link to="/Profile">
                  <ProfilePicture 
                    userId={user?.id}
                    userName={user?.full_name}
                    size="md" 
                    className={`cursor-pointer transition-all hover:ring-2 ${isDarkTheme ? 'hover:ring-slate-400' : 'hover:ring-brand-600'}`} 
                  />
                </Link>
              </div>
              
              {/* Profile - Tablet (768px - 1023px) */}
              <div className="hidden md:flex lg:hidden items-center mr-2 ml-auto">
                <Link to="/Profile">
                  <ProfilePicture 
                    userId={user?.id}
                    userName={user?.full_name}
                    size="md" 
                    className="cursor-pointer transition-all hover:ring-2 hover:ring-brand-600" 
                  />
                </Link>
              </div>
              
              {/* Profile - Mobile */}
              <div className="md:hidden flex items-center gap-2 mr-2 ml-auto">
                <Link to="/Profile">
                  <ProfilePicture 
                    userId={user?.id}
                    userName={user?.full_name}
                    size="md" 
                    className={`cursor-pointer transition-all hover:ring-2 ${isDarkTheme ? 'hover:ring-slate-400' : 'hover:ring-brand-600'}`} 
                  />
                </Link>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Main content - adjusted for sidebar */}
      <main className={`${currentPageName === "Discovery" ? "" : "pb-20 md:pb-0"} md:pl-[88px] lg:pl-[230px] ${mainBackgroundClasses} min-h-screen overflow-x-hidden max-w-full`}>
        {children}
      </main>

      {/* Feedback Button - Show on all pages except Landing and Program Detail pages */}
      {currentPageName !== "Landing" && !location.pathname.startsWith('/program/') && <FeedbackButton />}

      {/* Bottom Navigation - Mobile Only */}
      <nav className={`md:hidden fixed bottom-0 left-0 right-0 h-14 ${mobileNavBackgroundClasses} px-1 pb-1.5 pt-0 z-50 safe-area-inset-bottom shadow-[0_-12px_24px_-16px_rgba(15,23,42,0.65)]`}>
        <div className="relative w-full h-full">
          {/* Active tab indicator line */}
          {(() => {
            const activeIndex = navigationItems.findIndex(item => location.pathname === item.url);
            if (activeIndex === -1) return null;
            const tabWidth = 100 / navigationItems.length;
            const leftPosition = activeIndex * tabWidth;
            return (
              <div
                className="absolute top-0 h-0.5 bg-brand-500 transition-all duration-300 ease-in-out"
                style={{
                  left: `${leftPosition}%`,
                  width: `${tabWidth}%`
                }}
              />
            );
          })()}
          <div className="flex items-center justify-around h-full pt-1.5">
          {navigationItems.map((item) => {
            const isActive = location.pathname === item.url;
            // Material Design: With 5 destinations, show text labels only for active item
            // (or all items if we have 4 or fewer)
            const showLabel = isActive || navigationItems.length <= 4;

            return (
              <Ripple
                key={item.name}
                color={isActive ? 'brand' : isDarkTheme ? 'dark' : 'light'}
              >
                <button
                  onClick={() => navigate(item.url)}
                  className={`flex flex-col items-center justify-center gap-0.5 px-1 py-1 rounded-t-none rounded-b-lg transition-all duration-200 min-w-0 flex-1 max-w-[20%] min-h-[48px] -mt-1.5 pt-2.5 ${
                    isActive 
                      ? 'text-brand-500' 
                      : `${isDarkTheme ? 'text-slate-300' : 'text-slate-600'} hover:bg-black/5 active:bg-black/10`
                  }`}
                >
                  <Icon 
                    name={item.icon}
                    size="lg"
                    className={`flex-shrink-0 ${isActive ? 'text-brand-500' : isDarkTheme ? 'text-slate-300' : 'text-slate-600'}`} 
                  />
                  {showLabel && (
                    <span className={`text-xs leading-tight text-center break-words hyphens-auto ${mobileNavTextClasses(isActive)}`}>
                      {item.mobileLabel}
                    </span>
                  )}
                  {item.comingSoon && (
                    <span className="text-[8px] px-1 py-0.5 bg-gray-100 text-gray-600 rounded-full font-normal">
                      Soon
                    </span>
                  )}
                </button>
              </Ripple>
            );
          })}
          </div>
        </div>
      </nav>

      {/* Feedback Modal */}
      <FeedbackModal 
        isOpen={isFeedbackModalOpen} 
        onClose={handleCloseFeedbackModal}
      />
    </div>
  );
}
